import { openExistingAppointment, toEwsId } from "../../../../integrations/tools/outlook/appointment";
import { logOfficeError } from "../../../telemetry";
import type { CalEvent } from "../types";

/**
 * Open a link through Office's browser window (the task pane can't navigate
 * itself), falling back to a new window.
 */
export function openInBrowser(url: string, failureTag: string): void {
  try {
    if (typeof Office.context?.ui?.openBrowserWindow === "function") {
      Office.context.ui.openBrowserWindow(url);
      return;
    }
  } catch (err) {
    logOfficeError(err, failureTag);
  }
  window.open(url, "_blank", "noopener");
}

/** Open a meeting's Teams join link. */
export function openJoinLink(url: string): void {
  if (!url.startsWith("https://")) return;
  openInBrowser(url, "calendar-open-join-link");
}

/**
 * Open the meeting in Outlook, where anything AiVA can't do yet can be done
 * (an unsent draft is sent from there). It opens in the Outlook app; only when
 * Outlook reports it couldn't open the meeting does Outlook on the web open
 * instead, so the click never does nothing.
 */
export function openInOutlook(event: CalEvent): void {
  const web = event.webLink.startsWith("https://") ? event.webLink : "";
  const mailbox = Office.context?.mailbox;
  if (typeof mailbox?.displayAppointmentFormAsync === "function") {
    try {
      mailbox.displayAppointmentFormAsync(toEwsId(event.id), (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) return;
        logOfficeError(result.error, "calendar-open-in-outlook");
        if (web) openInBrowser(web, "calendar-open-in-outlook-web");
      });
      return;
    } catch (err) {
      logOfficeError(err, "calendar-open-in-outlook");
      if (web) {
        openInBrowser(web, "calendar-open-in-outlook-web");
        return;
      }
    }
  }
  openExistingAppointment(event.id, event.webLink);
}

/** Open an email (e.g. a meeting's thread) in the Outlook app, or in Outlook on the web if the app can't. */
export function openEmailInOutlook(messageId: string, webLink: string): void {
  const web = webLink.startsWith("https://") ? webLink : "";
  const mailbox = Office.context?.mailbox;
  const ewsId = toEwsId(messageId);
  if (typeof mailbox?.displayMessageFormAsync === "function") {
    try {
      mailbox.displayMessageFormAsync(ewsId, (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) return;
        logOfficeError(result.error, "calendar-open-email");
        if (web) openInBrowser(web, "calendar-open-email-web");
      });
      return;
    } catch (err) {
      logOfficeError(err, "calendar-open-email");
    }
  }
  // Outlook builds without the async call (e.g. classic Outlook for Windows) still have the original one.
  if (typeof mailbox?.displayMessageForm === "function") {
    try {
      mailbox.displayMessageForm(ewsId);
      return;
    } catch (err) {
      logOfficeError(err, "calendar-open-email");
    }
  }
  if (web) openInBrowser(web, "calendar-open-email-web");
}
